import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceArea, CartesianGrid, Dot,
} from "recharts";
import { formatDateAU } from "@/lib/formatDateAU";

/**
 * Water Health Score™ over time (Phase 2L-A108).
 *
 * Read-only visual on the multi-year Passport. Plots the SAME
 * `health_score` value the score gauge already shows for each
 * published test — no new scoring logic, no recalculation, just a
 * line chart of `passport_tests[].health_score` against
 * `passport_tests[].collected_at`.
 *
 * Visual conventions (consistent with parameter TrendChart.jsx):
 *   • 0–100 fixed y-axis
 *   • Excellent band (85+) shaded green
 *   • Action band (<50) shaded red so a contamination event is
 *     immediately visible
 *   • Newest result drawn as a larger emerald dot with a chip label
 *   • Mobile-friendly via ResponsiveContainer
 *   • Print-friendly: pure SVG, no images/canvas
 */

const BAND_COLOUR = {
  excellent: "#059669",   // emerald-600
  good:      "#0891B2",   // cyan-600
  fair:      "#D97706",   // amber-600
  attention: "#EA580C",   // orange-600
  action:    "#E11D48",   // rose-600
};

function bandFromScore(score) {
  if (score == null) return "good";
  if (score >= 85) return "excellent";
  if (score >= 70) return "good";
  if (score >= 55) return "fair";
  if (score >= 40) return "attention";
  return "action";
}

function LatestDot({ cx, cy, payload, dataLength, index }) {
  if (cx == null || cy == null) return null;
  const isLatest = index === dataLength - 1;
  const fill = BAND_COLOUR[payload.band] || "#06B6D4";
  if (isLatest) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={9} fill={fill} opacity={0.18} />
        <circle cx={cx} cy={cy} r={6} fill={fill} stroke="#fff" strokeWidth={2} />
      </g>
    );
  }
  return <circle cx={cx} cy={cy} r={4} fill={fill} />;
}

function ScoreTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl bg-white shadow-lg border border-slate-200 text-xs px-3 py-2">
      <div className="font-semibold text-slate-900">{p.date}</div>
      <div className="text-slate-500 capitalize">{p.band}</div>
      <div className="text-cyan-700 font-bold text-sm">{p.score}/100</div>
    </div>
  );
}

export function ScoreTrendChart({ tests, height = 240 }) {
  // `tests` arrive newest-first from the API; chart wants
  // oldest-left so reverse before mapping.
  const data = [...tests]
    .reverse()
    .filter((t) => t.health_score != null && t.collected_at)
    .map((t) => ({
      date: formatDateAU(t.collected_at),
      score: t.health_score,
      band: (t.band || bandFromScore(t.health_score)).toLowerCase(),
    }));

  if (data.length === 0) {
    return (
      <div
        data-testid="score-trend-empty"
        className="flex items-center justify-center bg-slate-50 rounded-2xl text-sm text-slate-400"
        style={{ height }}
      >
        Not enough published test data yet for a score trend.
      </div>
    );
  }

  const latest = data[data.length - 1];
  const minScore = Math.min(...data.map((d) => d.score));
  const showsRecoveryStory = minScore < 50 && latest.score >= 70;

  return (
    <div data-testid="score-trend-chart">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-cyan-700">
            Water Health Score™ over time
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {data.length} published test{data.length === 1 ? "" : "s"}
            {" · "}latest result {latest.score}/100 ({latest.band})
            {showsRecoveryStory && " · recovery after earlier dip"}
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> Excellent 85+
          </span>
          <span className="inline-flex items-center gap-1 text-rose-700">
            <span className="w-2 h-2 rounded-full bg-rose-500" /> Action &lt;50
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 12, right: 16, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tick={{ fontSize: 11, fill: "#64748B" }}
          />
          {/* Excellent band — green */}
          <ReferenceArea y1={85} y2={100} fill="#10B981" fillOpacity={0.10} />
          {/* Action band — red */}
          <ReferenceArea y1={0} y2={50} fill="#F43F5E" fillOpacity={0.07} />
          <Tooltip content={<ScoreTooltip />} />
          <Line
            type="monotone"
            dataKey="score"
            stroke="#0891B2"
            strokeWidth={2.5}
            isAnimationActive={false}
            dot={(props) => (
              <LatestDot {...props} dataLength={data.length} index={props.index} />
            )}
            activeDot={{ r: 7 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
